import React, { createContext, FC, ReactNode, useEffect, useRef, useState } from 'react';

import { useSessionContext } from '~/contexts/SessionContext';
import { getSidebarOrgTypes } from '~/utils/organization';

import { mainSidebar, SideBarContent, SideBarSection } from '../views/layouts/SideBar';
import { useCurrentOrgContext } from './CurrentOrgContext';

export interface SidebarContextProps {
  sidebarContent: SideBarContent;
  isSidebarNameChanged: boolean;
  setSidebarContent: (content: SideBarContent) => void;
}

interface SidebarProviderProps {
  children: ReactNode;
}

const SidebarContext = createContext<SidebarContextProps>({
  sidebarContent: undefined,
  isSidebarNameChanged: false,
  setSidebarContent: undefined,
});

const isDetailsPagePath = (pathname: string) =>
  /^\/app\/(organizations|audiences)\/[^/]+(?:\/(?!create-segment)|$)/.test(pathname);

// Audience detail pages keep the main sidebar (their sub-tabs live in the header), unlike
// organization detail pages, which still push a dedicated detail sidebar.
const isOrganizationDetailsPagePath = (pathname: string) =>
  isDetailsPagePath(pathname) && !/^\/app\/audiences\//.test(pathname);

export const SidebarProvider: FC<SidebarProviderProps> = ({ children }) => {
  const { orgMenuItemTypes, hasAudienceOnlyAccess, hasInviteToAnOrganization, hasAccessToAudience, loading } =
    useSessionContext();
  // CurrentOrgProvider wraps SidebarProvider (see App.tsx), so activeOrg is available here for
  // first-paint consistency with the AppContainer effect that keeps this in sync afterwards.
  const { activeOrg } = useCurrentOrgContext();
  const sidebarOrgTypes = getSidebarOrgTypes(activeOrg, orgMenuItemTypes);
  const [sidebarContent, setContentLocal] = useState<SideBarContent>(() =>
    isOrganizationDetailsPagePath(window.location.pathname)
      ? { name: 'detail', sections: [{ items: [] }] }
      : mainSidebar(sidebarOrgTypes, hasAudienceOnlyAccess, hasInviteToAnOrganization, hasAccessToAudience, loading),
  );

  // prevNameRef always reflects the name of the last committed content — updated
  // synchronously inside setContent so there is never a stale-closure window.
  const prevNameRef = useRef(sidebarContent.name);
  const [isSidebarNameChanged, setIsSidebarNameChanged] = useState(false);

  // Reset the flag after one render so the CSS animation fires only once.
  useEffect(() => {
    if (isSidebarNameChanged) {
      setIsSidebarNameChanged(false);
    }
  }, [isSidebarNameChanged]);

  const isEqualSections = (sections1: SideBarSection[], sections2: SideBarSection[]) => {
    const stringifySections = (sectionsArray: SideBarSection[]) =>
      JSON.stringify(sectionsArray, (key, value) => (key !== 'icon' ? value : undefined));
    return stringifySections(sections1) === stringifySections(sections2);
  };

  const setContent = (contentNew: SideBarContent) => {
    if (!isEqualSections(sidebarContent?.sections, contentNew?.sections)) {
      const nameChanged = contentNew.name !== prevNameRef.current;
      prevNameRef.current = contentNew.name;
      setIsSidebarNameChanged(nameChanged);
      setContentLocal(contentNew);
    }
  };

  return (
    <SidebarContext.Provider
      value={{
        sidebarContent,
        isSidebarNameChanged,
        setSidebarContent: setContent,
      }}
    >
      {children}
    </SidebarContext.Provider>
  );
};

export const useSidebarContext: () => SidebarContextProps = () => {
  return React.useContext(SidebarContext);
};
